/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import type { FC } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useMutation,
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';
import React, {
	Fragment,
	useContext,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import type { DataProxy } from 'apollo-cache';
import { useIntl, defineMessages, FormattedMessage } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import type { EditorActions } from '@atlaskit/editor-core';
import type { AnnotationInfo } from '@atlaskit/editor-plugins/annotation';
import { AnnotationUpdateEvent } from '@atlaskit/editor-common/types';
import { AnnotationMarkStates, AnnotationTypes } from '@atlaskit/adf-schema';
import type { AddMarkStep, Step } from '@atlaskit/editor-prosemirror/transform';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer';
import { ChromelessEditor } from '@atlaskit/editor-core/appearance-editor-chromeless';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box } from '@atlaskit/primitives';

import { usePageClass } from '@confluence/comments-hooks/entry-points/usePageClass';
import { useAddCommentPermissionCheck } from '@confluence/comments-hooks/entry-points/useAddCommentPermissionCheck';
import { useInlineCommentsDispatchContext } from '@confluence/comment-context/entry-points/InlineCommentsContext';
import { useCommentsContentActions } from '@confluence/comment-context/entry-points/useCommentsContent';
import { useEmbeddedComments } from '@confluence/embedded-confluence-allowed-features/entry-points/comments';
import { ThirdPartyNudge } from '@confluence/third-party-nudge';
import type { InlineCommentAuthorKnownUser } from '@confluence/inline-comments-queries/entry-points/InlineCommentsQuery';
import type { ContentRepresentation } from '@confluence/inline-comments-queries/entry-points/CreateInlineReplyMutation';
import type {
	ActiveInlineCommentsQueryType,
	CommentInlineResolveProperties,
	ActiveInlineCommentNodesType,
} from '@confluence/inline-comments-queries/entry-points/ActiveInlineCommentsQuery';
import type {
	InlineCommentQueryType,
	InlineCommentQueryVariables,
	GraphQLContentStatus,
	InlineCommentLocation,
} from '@confluence/inline-comments-queries/entry-points/InlineCommentQuery';
import { useShouldCommentSidebarBeSticky } from '@confluence/inline-comments-hooks/entry-points/useShouldCommentSidebarBeSticky';
import { useCommentSidebarOffset } from '@confluence/inline-comments-hooks/entry-points/useCommentSidebarOffset';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode';
import { useUnreadCommentsForInlineComment } from '@confluence/unread-comments/entry-points/useUnreadCommentsForInlineComment';
import { useUnreadComments } from '@confluence/unread-comments/entry-points/useUnreadComments';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay';
import { InlineCommentFramework } from '@confluence/inline-comments-utils/entry-points/enum';
import { getRendererAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getRendererAnnotationEventEmitter';
import { getEditorAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getEditorAnnotationEventEmitter';
import {
	useAnnotationsDispatch,
	useAnnotations,
} from '@confluence/annotation-provider-store/entry-points/AnnotationsContext';
import { PageSegmentLoadEnd } from '@confluence/browser-metrics/entry-points/PageSegmentLoadEnd';
import { LocationValue } from '@confluence/comments-panel-utils/entry-points/commentsPanelHelper';
import {
	useCommentsPanelState,
	useCommentsPanelActions,
} from '@confluence/comments-panel-utils/entry-points/useCommentsPanel';
import {
	VIEW_INLINE_COMMENT_EXPERIENCE,
	RESOLVE_INLINE_COMMENT_EXPERIENCE,
	REPLY_TO_INLINE_COMMENT_EXPERIENCE,
	REPLY_TO_INLINE_COMMENT_LOAD_EXPERIENCE,
	REPLY_TO_INLINE_COMMENT_PUBLISH_EXPERIENCE,
	EDIT_INLINE_COMMENT_EXPERIENCE,
	DELETE_INLINE_COMMENT_EXPERIENCE,
	SELECT_INLINE_COMMENT_EXPERIENCE,
	VIEW_PAGE_DIRECT_COMMENT_DATA_FLOW_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component';
import {
	selectInlineComment,
	selectInlineCommentCompoundExperience,
} from '@confluence/experience-tracker/entry-points/selectInlineCommentCompoundExperience';
import { startEditorSubExperience } from '@confluence/experience-tracker/entry-points/startExperienceForRoute';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout';
import { usePageContentId } from '@confluence/page-context';
import { clearCommentDraft } from '@confluence/comment/entry-points/draftUtils';
import { useInlineCommentQueryParams } from '@confluence/comment/entry-points/useCommentQueryParams';
import { CommentEditor } from '@confluence/comment/entry-points/LazyCommentEditor';
import { CommentDeletionLocation } from '@confluence/inline-comments-queries/entry-points/DeleteInlineCommentMutation';
import { CommentCreationLocation } from '@confluence/inline-comments-queries/entry-points/CreateInlineCommentMutation';
import { InlineCommentQuery } from '@confluence/inline-comments-queries/entry-points/InlineCommentQuery.graphql';
import { ActiveInlineCommentsQuery } from '@confluence/inline-comments-queries/entry-points/ActiveInlineCommentsQuery.graphql';
import { expVal } from '@confluence/feature-experiments';
import {
	useAllCommentsData,
	buildDefaultCommentsContentStatusFilters,
} from '@confluence/comments-data-manager/entry-points/useAllCommentsData';
import {
	showResolvedCommentFlag,
	createHandleReopenComment,
} from '@confluence/inline-comments-common/entry-points/showResolvedCommentFlag';
import { CollapsedReplies } from '@confluence/inline-comments-common/entry-points/CollapsedReplies';
import { ReopenedMessage } from '@confluence/inline-comments-common/entry-points/ReopenedMessage';
import { EditComment } from '@confluence/inline-comments-common/entry-points/EditComment';
import { CommentBody } from '@confluence/inline-comments-common/entry-points/CommentBody';
import { CommentAuthor } from '@confluence/inline-comments-common/entry-points/CommentAuthor';
import { fg } from '@confluence/feature-gating';
import { PageMode } from '@confluence/page-utils/entry-points/enums';
import {
	getTargetNodeType,
	handleResolveSuccess,
	handleDeleteSuccess,
	handleMutationFailure,
	handleCreateReplySuccess,
	updateApolloCacheCallback,
	getTranslatedError,
	isAlreadyDeletedError,
	isNetworkError,
	updateApolloCacheCallbackForGA,
	parseError,
	updateReactionsCacheCallbackForGA,
	defaultReactionsSummary,
} from '@confluence/inline-comments-common/entry-points/inlineCommentsUtils';
import type {
	MutationResult,
	CommentPermissions,
	CommentAction,
} from '@confluence/inline-comments-common/entry-points/inlineCommentsTypes';
import {
	ReplyListContainer,
	NewReplyContainer,
} from '@confluence/inline-comments-common/entry-points/styled';
import { i18n as inlineCommentsI18n } from '@confluence/inline-comments-common/entry-points/i18n';
import { commentsi18n } from '@confluence/comments-util/entry-points/commentsi18n';
import { handleScrollToElement } from '@confluence/comments-util/entry-points/domUtils';
import { constructStepForGql } from '@confluence/comments-util/entry-points/rendererAnnotations';
import { useInlineCommentsActions } from '@confluence/inline-comments-hooks/entry-points/useInlineComments';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';
import { getLogger } from '@confluence/logger';
import { usePageInfo } from '@confluence/page-info/entry-points/usePageInfo';
import { normalizeCommentData } from '@confluence/comments-data/entry-points/commentsDataHelper';
import {
	CommentActionType,
	useCommentsData,
	UnreadAction,
	CommentType,
	type ReplyData,
	type CommentData,
} from '@confluence/comments-data/entry-points/useCommentsData';
import { useWindowSize } from '@confluence/dom-helpers/entry-points/useWindowSize';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { AnalyticsSource } from '@confluence/comments-util/entry-points/analytics';
import { withFlags } from '@confluence/flags/entry-points/withFlags';
import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags';
import { CommentReplies } from '@confluence/comments-panel/entry-points/CommentReplies';
import { getAdfForQuickReplyMention } from '@confluence/comments-panel/entry-points/commentThreadHelper';
import { useShowCommentsPanel } from '@confluence/comments-panel/entry-points/useShowCommentsPanel';
import { formatTimestampForAriaLabel } from '@confluence/comments-util/entry-points/dateUtils';
import { useGetCommentStatus } from '@confluence/comments-util/entry-points/commentStatus';
import { selectUpdateFunction } from '@confluence/comments-data-manager/entry-points/commentDataManagerUtils';

import type {
	InlineCommentResolveInlineCommentMutation as InlineCommentResolveInlineCommentMutation$data,
	InlineCommentResolveInlineCommentMutationVariables as InlineCommentResolveInlineCommentMutation$variables,
} from './__types__/InlineCommentResolveInlineCommentMutation';
import type {
	InlineCommentCreateInlineReplyMutation as InlineCommentCreateInlineReplyMutation$data,
	InlineCommentCreateInlineReplyMutationVariables as InlineCommentCreateInlineReplyMutation$variables,
} from './__types__/InlineCommentCreateInlineReplyMutation';
import {
	DIRECT_COMMENT_DATA_FLOW_METRIC,
	EDITOR_INLINE_COMMENT_RENDER_METRIC,
	RENDERER_INLINE_COMMENT_RENDER_METRIC,
} from './perf.config';
import { MAX_UNCOLLAPSED_REPIES } from './inlineCommentConstants';
import { type CommentNavigationOptions, CommentSidebar } from './CommentSidebar';

const relativeFilePath = 'annotation-provider-inline-comments/InlineComment';

const InlineCommentResolveInlineCommentMutation = gql`
	mutation InlineCommentResolveInlineCommentMutation($commentId: ID!, $resolved: Boolean!) {
		resolveInlineComment(commentId: $commentId, resolved: $resolved, dangling: false) {
			status
			resolveProperties {
				resolved
				resolvedUser
				isDangling
				resolvedFriendlyDate
				resolvedTime
			}
		}
	}
`;
const InlineCommentDeleteInlineCommentMutation = gql`
	mutation InlineCommentDeleteInlineCommentMutation(
		$commentIdToDelete: ID!
		$deleteFrom: CommentDeletionLocation
	) {
		deleteComment(commentIdToDelete: $commentIdToDelete, deleteFrom: $deleteFrom)
	}
`;
const InlineCommentDeleteInlineCommentMutationWithStepMutation = gql`
	mutation InlineCommentDeleteInlineCommentMutationWithStepMutation(
		$input: DeleteInlineCommentInput!
	) {
		deleteInlineComment(input: $input)
	}
`;
const InlineCommentCreateInlineReplyMutation = gql`
	mutation InlineCommentCreateInlineReplyMutation(
		$input: ReplyInlineCommentInput!
		$pageId: ID!
		$includeReactions: Boolean!
	) {
		replyInlineComment(input: $input) {
			id
			body(representation: ATLAS_DOC_FORMAT) {
				value
			}
			contentStatus
			createdAt {
				value
			}
			createdAtNonLocalized
			links {
				webui
				editui
			}
			location {
				type
				... on InlineComment {
					inlineMarkerRef
					inlineText
					inlineResolveProperties {
						resolved
						isDangling
						resolvedFriendlyDate
						resolvedTime
						resolvedUser
					}
				}
				... on FooterComment {
					commentResolveProperties {
						resolved
						resolvedFriendlyDate
						resolvedTime
						resolvedUser
					}
				}
			}
			parentId
			permissions {
				isEditable
				isRemovable
				isResolvable
			}
			version {
				by {
					displayName
				}
				when
				number
			}
			author {
				displayName
				profilePicture {
					path
				}
				permissionType
				... on KnownUser {
					accountId
				}
			}
			createdAt {
				value
			}
			createdAtNonLocalized
			reactionsSummary(pageId: $pageId, contentType: "page", childType: "comment")
				@include(if: $includeReactions) {
				ari
				containerAri
				reactionsCount
				reactionsSummaryForEmoji {
					id
					emojiId
					count
					reacted
				}
			}
		}
	}
`;

const i18n = defineMessages({
	cannotDeleteTitle: {
		id: 'annotation-provider-inline-comments.could.not.delete.title',
		defaultMessage: 'Unable to delete comment',
		description:
			'The title of the flag shown to a user that says their comment could not be deleted',
	},
	commentLoadError: {
		id: 'annotation-provider-inline-comments.error.comment.load.message',
		defaultMessage:
			'Something went wrong when loading this comment. Refresh to view the latest updates.',
		description:
			'A generic message to inform the user that there was an error in loading a comment',
	},
	commentLoadNetworkError: {
		id: 'annotation-provider-inline-comments.network.error.message',
		defaultMessage:
			"This comment wasn't loaded due to a network error. Check your connection and try again.",
		description: 'A message to inform the user that there was a network error in loading a comment',
	},
	commentLoadErrorTitle: {
		id: 'annotation-provider-inline-comments.error.comment.load.title',
		defaultMessage: 'Unable to load comment',
		description:
			'A title for the generic message informing the user that there was an error in loading a comment',
	},
	commentReplyAriaLabel: {
		id: 'annotation-provider-inline-comments.comment.reply.aria-label',
		defaultMessage: 'Reply to {name}’s thread',
		description: 'Reply placeholder for comment thread, with the comment author name',
	},
	commentAriaLabel: {
		id: 'annotation-provider-inline-comments.comment.aria-label',
		defaultMessage: 'Comment by {displayName}, {timestamp}',
		description: 'Comment aria label, with the comment author name and timestamp',
	},
});

const logger = getLogger('annotation-provider-inline-comment');
type ActionResult =
	| {
			step: Step;
			doc: JSONDocNode;
	  }
	| false;
type DeleteOptions = {
	deleteAnnotation: (annotationInfo: AnnotationInfo) => ActionResult;
	onDeleteSuccess: (doc: JSONDocNode, annotationId: string) => void;
};

type InlineCommentProps = {
	editorActions?: EditorActions;
	isEditor?: boolean;
	isArchived?: boolean;
	onNavigationClick: (nextMarkerRef: string) => void;
	annotation: AnnotationInfo;
	/**
	 * Return a list of inline node types, which are wrapped by the annotation,
	 * for annotation with given ID.
	 *
	 * The `undefined` will be returned if `editor_inline_comments_on_inline_nodes` is off.
	 *
	 * @todo: Do not forget to remove `undefined` when the
	 *        `editor_inline_comments_on_inline_nodes` is removed.
	 */
	getInlineNodeTypes: (annotationId: string) => string[] | undefined;
	onResolve: (annotationId: string) => void;
	deleteOptions?: DeleteOptions;
	onClose?: () => void;
	onDelete?: (annotationId: string) => void;
	supportedTopLevelActions?: CommentAction[];
	isOpeningMediaCommentFromToolbar?: boolean;
	inlineCommentRef?: (node: any) => void;
	isInlineCommentVisible?: boolean;
	isOffline?: boolean;
};

type ResolvedComment = {
	commentId: string;
	inlineMarkerRef: string;
};

const InlineCommentComponent: FC<InlineCommentProps & WithFlagsProps> = ({
	annotation: selectedAnnotation,
	onResolve,
	deleteOptions,
	onClose,
	isEditor,
	isArchived,
	onNavigationClick,
	onDelete,
	supportedTopLevelActions,
	getInlineNodeTypes,
	isOpeningMediaCommentFromToolbar,
	inlineCommentRef = () => {},
	isInlineCommentVisible = false,
	flags,
	isOffline,
}) => {
	const pageClass = usePageClass();
	const lastSelected = useRef<string | undefined>(undefined);
	const { replyToCommentUser } = useCommentsPanelState();
	const { setCurrentView, setReplyToCommentId, setCurrentlySelectedThreadKey } =
		useCommentsPanelActions();
	const pageMode = useGetPageMode();
	const [contentId] = usePageContentId();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const {
		focusedCommentId,
		replyToCommentId,
		editCommentId: editCommentQueryId,
	} = useInlineCommentQueryParams();

	const allowComments = useEmbeddedComments();

	const RENDER_METRIC = isEditor
		? EDITOR_INLINE_COMMENT_RENDER_METRIC
		: RENDERER_INLINE_COMMENT_RENDER_METRIC;

	if (lastSelected.current !== selectedAnnotation.id) {
		selectInlineCommentCompoundExperience.showComment.start({
			pageClass,
			annotationId: selectedAnnotation.id,
			entryPoint: 'unknown:InlineComment',
		});

		if (fg('confluence_frontend_view_inline_comment_exp_parity')) {
			RENDER_METRIC.start();

			const attributes = {
				pageMode,
				pageClass,
				framework: InlineCommentFramework.ANNOTATION_PROVIDER,
				annotationId: selectedAnnotation.id,
				isEditor: !!isEditor,
				isArchived: !!isArchived,
				isOpeningMediaCommentFromToolbar: !!isOpeningMediaCommentFromToolbar,
				isInlineCommentVisible: !!isInlineCommentVisible,
				isOffline: !!isOffline,
				...(focusedCommentId && { focusedCommentId }),
				...(replyToCommentId && { replyToCommentId }),
				...(editCommentQueryId && { editCommentId: editCommentQueryId }),
			};

			if (fg('confluence_frontend_view_inline_comment_extend')) {
				selectInlineComment.debug(
					`InlineComment component mounted with annotation id ${selectedAnnotation.id}`,
				);

				experienceTracker.updateAttributes({
					name: VIEW_INLINE_COMMENT_EXPERIENCE,
					attributes,
				});
			} else {
				experienceTracker.start({
					name: VIEW_INLINE_COMMENT_EXPERIENCE,
					timeout: ExperienceTimeout.VIEW_INLINE_COMMENT,
					attributes: {
						...attributes,
						entryPoint: relativeFilePath,
					},
				});
			}

			if (pageClass !== 'renderer' && pageClass !== 'editor') {
				experienceTracker.abort({
					name: VIEW_INLINE_COMMENT_EXPERIENCE,
					reason: 'Page class is not renderer or editor',
					checkForTimeout: true,
					publishDebugPoints: true,
					attributes: {
						stopPoint: relativeFilePath,
					},
				});
			}
		}

		lastSelected.current = selectedAnnotation.id;
	}

	const annotationElementRef = useRef<HTMLElement | null>(null);
	const resolveWindowCloseTimeout = useRef<ReturnType<typeof setTimeout> | undefined>();
	const sidebarEl = useRef<HTMLDivElement | null>(null);
	const commentContainerElementRef = useRef<HTMLDivElement | null>(null);
	const [eventEmitter] = useState(() =>
		isEditor ? getEditorAnnotationEventEmitter() : getRendererAnnotationEventEmitter(),
	);
	const [shouldFocusNewReply, setShouldFocusNewReply] = useState(false);
	// @ts-ignore FIXME: `contentId` can be `undefined` here, and needs proper handling
	const pageId: string = contentId;
	const { addUnresolvedInlineComment, removeUnresolvedInlineComment } = useInlineCommentsActions();
	const isLivePage = useIsCurrentPageLive();
	const { canAddComments } = useAddCommentPermissionCheck(pageId);

	const {
		handleClose,
		setEditorCreateProps,
		setHoverProps,
		setSelectionProps,
		setStoredCommentData,
		clearStoredCommentData,
	} = useAnnotationsDispatch();
	const { storedCommentData } = useAnnotations();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { userId: currentUserId } = useSessionData();
	const { width: windowWidth } = useWindowSize();

	const [editCommentId, setCommentForEdit] = useState('');
	const [resolvedComment, setResolvedComment] = useState<ResolvedComment | null>(null);
	const [shouldRender, setShouldRender] = useState(false);

	const [isReplyChainCollapsed, setIsReplyChainCollapsed] = useState(true);
	const { onChange, resetContentChanged } = useCommentsContentActions();
	const { setActiveHighlight } = useInlineCommentsDispatchContext();
	const { isCommentsPanelShown, showCommentsPanel } = useShowCommentsPanel();
	const nudgeRef = useRef<HTMLDivElement | null>(null);
	const [popupsScrollableElement, setPopupsScrollableElement] = React.useState<
		HTMLElement | undefined
	>();

	const intl = useIntl();
	const { formatMessage } = intl;

	const { isSticky } = useShouldCommentSidebarBeSticky({
		annotationElement: annotationElementRef.current,
		isEditor: !!isEditor,
	});

	const sidebarOffset = useCommentSidebarOffset({
		isEditor: isEditor || false,
		annotationElement: document.getElementById(selectedAnnotation.id),
		isViewCommentMode: true,
		windowWidth,
		isSticky,
	});

	const apolloCacheCallback = fg('confluence_frontend_content_wrapper')
		? updateApolloCacheCallbackForGA
		: updateApolloCacheCallback;

	const handleCloseCreateComment = useCallback(() => {
		handleClose();
		setHoverProps(null);
		setSelectionProps(null);
		setEditorCreateProps(null);
	}, [handleClose, setHoverProps, setSelectionProps, setEditorCreateProps]);

	const onlyHasReactionsSummaryErrors = useCallback((error: any) => {
		let hasOtherGqlErrors = false;
		if (error.graphQLErrors?.length > 0) {
			error.graphQLErrors.forEach((e: any) => {
				if (e.path?.includes('reactionsSummary')) {
					selectInlineComment.debug('found reactionsSummary graphql error', {
						errorMessage: e.message,
					});
				} else {
					hasOtherGqlErrors = true;
				}
			});
		}
		return !hasOtherGqlErrors && !error.networkError;
	}, []);

	const handleGqlError = useCallback(
		(error: Error) => {
			selectInlineComment.debug('received a gql error', { errorMessage: error.message });

			if (onlyHasReactionsSummaryErrors(error)) {
				selectInlineComment.debug('did not receive non-reactionsSummary errors, ignoring');
				return;
			}

			// If the error is reactions based, just set it to null and move on
			if (error.message.search(/reactions/i) !== -1 || error.message.search(/cc-pages/i) !== -1) {
				markErrorAsHandled(error);
			} else if (!fg('confluence_frontend_view_inline_comment_exp_parity')) {
				experienceTracker.stopOnError({
					name: VIEW_INLINE_COMMENT_EXPERIENCE,
					error,
				});
			}

			const parsedError = parseError(error);
			const translatedError = getTranslatedError(parsedError.message);

			if (translatedError === inlineCommentsI18n.unexpectedError) {
				void flags.showWarningFlag({
					title: <FormattedMessage {...i18n.commentLoadErrorTitle} />,
					description: <FormattedMessage {...i18n.commentLoadError} />,
				});

				selectInlineCommentCompoundExperience.showComment.fail(error);

				if (fg('confluence_frontend_view_inline_comment_exp_parity')) {
					experienceTracker.stopOnError({
						name: VIEW_INLINE_COMMENT_EXPERIENCE,
						error,
						attributes: {
							stopPoint: relativeFilePath,
						},
					});
				}
			} else {
				void flags.showWarningFlag({
					title: <FormattedMessage {...i18n.commentLoadErrorTitle} />,
					description: (
						<FormattedMessage
							{...(isNetworkError(translatedError)
								? i18n.commentLoadNetworkError
								: translatedError)}
						/>
					),
				});

				selectInlineCommentCompoundExperience.showComment.abort({
					abortReason: parsedError.message as any,
				});

				if (fg('confluence_frontend_view_inline_comment_exp_parity')) {
					experienceTracker.abort({
						name: VIEW_INLINE_COMMENT_EXPERIENCE,
						reason: parsedError.message,
						checkForTimeout: true,
						publishDebugPoints: true,
						attributes: {
							stopPoint: relativeFilePath,
						},
					});
				}

				markErrorAsHandled(error);
			}

			selectInlineComment.debug('Closing create comment following a gql error');
			// Close the create comment component if there was an error loading the newly created comment
			handleCloseCreateComment();
		},
		[experienceTracker, flags, handleCloseCreateComment, onlyHasReactionsSummaryErrors],
	);

	const [{ readCommentsListState, unreadCommentIDSet }] = useUnreadComments();

	// Determine if we should use cache-only policy or skip query entirely
	const shouldSkipQuery = Boolean(storedCommentData);

	// When experiment is enabled, use useAllCommentsData to find comment directly
	const allCommentsData = useAllCommentsData({
		contentId: pageId,
		contentStatus: buildDefaultCommentsContentStatusFilters(pageMode),
		options: {
			skip: !expVal('cc_comments_new_comments_data_manager', 'isEnabled', false) || shouldSkipQuery,
		},
	});
	const { deleteCommentInCache } = allCommentsData;

	// Find comment directly from useAllCommentsData by matching markerRef (top-level)
	const commentFromAllCommentsData = useMemo(() => {
		if (!expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
			return undefined;
		}

		// Find top-level comment by matching markerRef
		// Note that we are only looking at top-level comments here, plus the replies will all the same
		// markerRef as their parent comment anyway
		for (const comment of allCommentsData.allComments || []) {
			if (!comment) {
				continue;
			}
			const location = comment.location as { inlineMarkerRef?: string } | null;
			if (location?.inlineMarkerRef === selectedAnnotation.id) {
				return comment;
			}
		}

		return undefined;
	}, [allCommentsData.allComments, selectedAnnotation.id]);

	const { data, error: gqlError } = useQuery<
		ActiveInlineCommentsQueryType | InlineCommentQueryType
	>(fg('confluence_frontend_content_wrapper') ? ActiveInlineCommentsQuery : InlineCommentQuery, {
		variables: fg('confluence_frontend_content_wrapper')
			? {
					pageId,
					inlineMarkerRefList: [selectedAnnotation.id],
					contentStatus: [isArchived ? 'ARCHIVED' : 'DRAFT', 'CURRENT'] as GraphQLContentStatus[],
				}
			: {
					pageId,
					annotationId: selectedAnnotation.id,
					contentStatus: [isArchived ? 'ARCHIVED' : 'DRAFT', 'CURRENT'] as GraphQLContentStatus[],
				},
		onError: undefined,
		fetchPolicy: 'cache-and-network',
		skip: shouldSkipQuery || expVal('cc_comments_new_comments_data_manager', 'isEnabled', false),
		// This allows rendering using data from a partially-successful graphql query.
		// If query receives an error, `gqlError` will be set, while any successfully
		// fetched data will be available in `data` and be rendered.
		errorPolicy: 'all',
	});

	// Use initial comment data, new hook data, or query data
	const effectiveData = useMemo(() => {
		if (
			commentFromAllCommentsData &&
			expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
		) {
			return { comments: { nodes: [commentFromAllCommentsData] } };
		}
		if (shouldSkipQuery) {
			return { comments: { nodes: [storedCommentData] } };
		}
		return data;
	}, [shouldSkipQuery, storedCommentData, commentFromAllCommentsData, data]);

	const [reopenInlineCommentFn] = useMutation<
		InlineCommentResolveInlineCommentMutation$data,
		InlineCommentResolveInlineCommentMutation$variables
	>(InlineCommentResolveInlineCommentMutation);

	const { pageInfo, error: pageInfoError } = usePageInfo();

	useEffect(() => {
		if (pageInfoError && fg('confluence_frontend_inline_comment_page_info_err')) {
			handleGqlError(pageInfoError);
		}
	}, [pageInfoError, handleGqlError]);

	useEffect(() => {
		if (gqlError) {
			handleGqlError(gqlError);
		}
	}, [gqlError, handleGqlError]);

	// When the selectedAnnotation changes make sure we unset whatever resolved logic exists
	useEffect(() => {
		setResolvedComment(null);
		clearTimeout(resolveWindowCloseTimeout.current);
		resolveWindowCloseTimeout.current = undefined;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedAnnotation]);

	// eslint-disable-next-line graphql-client-usage/graphql-generic-types
	const [resolveInlineCommentFn] = useMutation(InlineCommentResolveInlineCommentMutation);
	// eslint-disable-next-line graphql-client-usage/graphql-generic-types
	const [deleteInlineCommentFn] = useMutation(InlineCommentDeleteInlineCommentMutation);
	// eslint-disable-next-line graphql-client-usage/graphql-generic-types
	const [deleteInlineCommentWithStepFn] = useMutation(
		InlineCommentDeleteInlineCommentMutationWithStepMutation,
	);
	const [createReplyFn] = useMutation<
		InlineCommentCreateInlineReplyMutation$data,
		InlineCommentCreateInlineReplyMutation$variables
	>(InlineCommentCreateInlineReplyMutation);
	// Editor setup
	const spaceId = pageInfo?.space?.id ?? '';
	const pageType = pageInfo?.type ?? '';

	// User page permissions
	const operations = pageInfo?.operations || [];

	const canAddComment = operations.some(
		(op) => op?.operation === 'create' && op?.targetType === 'comment',
	);

	// The user can upload media only if they have update permissions for the page if confluence_comment_use_comment_media_endpoint FG is off
	const hasMediaUploadPermissions = fg('confluence_comment_use_comment_media_endpoint')
		? canAddComment
		: operations.some((op) => op?.operation === 'update' && op?.targetType === pageType);

	// Comment metadata
	let commentData;
	if (
		commentFromAllCommentsData &&
		expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
	) {
		commentData = commentFromAllCommentsData; // Use comment from useAllCommentsData
	} else if (shouldSkipQuery) {
		commentData = storedCommentData; // Use initial comment data directly
	} else if (fg('confluence_frontend_content_wrapper')) {
		commentData = (effectiveData as ActiveInlineCommentsQueryType)?.comments?.nodes?.[0];
	} else {
		commentData = (effectiveData as InlineCommentQueryType)?.comments?.nodes?.[0];
	}

	// Track experiment completion when comment data is loaded and rendered
	useEffect(() => {
		// Only track when we have comment data and the component is visible
		// And only when the experiment is running
		if (commentData && isInlineCommentVisible) {
			experienceTracker.succeed({
				name: VIEW_PAGE_DIRECT_COMMENT_DATA_FLOW_EXPERIENCE,
				attributes: {
					annotationId: selectedAnnotation.id,
					pageId,
					pageMode: isEditor ? PageMode.EDIT : PageMode.VIEW,
					isEditor: Boolean(isEditor),
				},
			});

			DIRECT_COMMENT_DATA_FLOW_METRIC.stop();
		}
	}, [
		commentData,
		isInlineCommentVisible,
		storedCommentData,
		selectedAnnotation.id,
		pageId,
		isEditor,
		experienceTracker,
	]);

	const commentStatus = useGetCommentStatus({
		resolveProperties:
			(commentData?.location as { inlineResolveProperties?: CommentInlineResolveProperties })
				?.inlineResolveProperties ||
			(commentData?.location as { commentResolveProperties?: CommentInlineResolveProperties })
				?.commentResolveProperties,
	});

	const topCommentUserId = (commentData?.author as InlineCommentAuthorKnownUser)?.accountId;
	const topCommentUserAvatar = commentData?.author?.profilePicture?.path;
	const topCommentDisplayName =
		commentData?.author?.displayName ?? formatMessage(commentsi18n.anonymous);
	const commentId = commentData?.id;
	const createdAt = commentData?.createdAt?.value;
	const createdAtNonLocalized = commentData?.createdAtNonLocalized;
	const when = commentData?.version?.when;
	const versionByDisplayName = commentData?.version?.by?.displayName;
	const isEdited = (commentData?.version?.number || 0) > 1;
	const permissions = commentData?.permissions;
	const content = commentData?.body?.value;
	const permissionType = commentData?.author?.permissionType;
	const numReplies = commentData?.replies?.length || 0;
	const commentThreadLength = 1 + numReplies; // includes root comment

	const resolveProperties =
		(commentData?.location as InlineCommentLocation)?.inlineResolveProperties || undefined;
	const resolvedTime = (commentData?.location as InlineCommentLocation)?.inlineResolveProperties
		?.resolvedTime;
	const resolvedUser = (commentData?.location as InlineCommentLocation)?.inlineResolveProperties
		?.resolvedUser;
	const commentUrl = isEditor && !isLivePage ? commentData?.links.editui : commentData?.links.webui;
	const reactionsSummary = commentData?.reactionsSummary ?? null;

	const shouldCollapseReplies =
		isReplyChainCollapsed && numReplies && numReplies > MAX_UNCOLLAPSED_REPIES;

	const [
		{ orderedActiveAnnotationIdList },
		{
			addReplyToCommentThread,
			handleRemovingComments,
			deleteParentComment,
			resolveParentComment,
			reopenParentComment,
			setOrderedActiveAnnotationIdList,
			updateUnreadStatus,
		},
	] = useCommentsData();

	const handleUpdateUnreadStatus = useCallback(
		(commentIds: Set<string>) => {
			// Update the Comments Panel's data cache that this comment has been read
			updateUnreadStatus(
				{
					inline: {
						[selectedAnnotation.id]: commentIds,
					},
					general: {},
				},
				UnreadAction.READ,
			);
		},
		[updateUnreadStatus, selectedAnnotation.id],
	);

	const { unreadCommentRefs, currentUnreadIds, markCommentReadError } =
		useUnreadCommentsForInlineComment({
			commentData,
			isEditor: Boolean(isEditor),
			focusedCommentId: focusedCommentId ?? null,
			shouldCollapseReplies: Boolean(shouldCollapseReplies),
			setIsReplyChainCollapsed,
			isInlineCommentVisible,
			onUpdateUnreadStatus: handleUpdateUnreadStatus,
		});

	useEffect(() => {
		if (!fg('confluence_frontend_view_inline_comment_exp_parity')) {
			RENDER_METRIC.start();

			experienceTracker.start({
				name: VIEW_INLINE_COMMENT_EXPERIENCE,
				id: selectedAnnotation.id,
				attributes: {
					mode: pageMode,
					framework: InlineCommentFramework.ANNOTATION_PROVIDER,
				},
			});
		}
	}, [RENDER_METRIC, selectedAnnotation, experienceTracker, isEditor, pageMode]);

	useEffect(() => {
		if (commentId) {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'viewed',
					actionSubject: 'inlineCommentsSidebar',
					objectType: 'page',
					objectId: pageId,
					source: isEditor ? 'editPageScreen' : 'viewPageScreen',
					attributes: {
						mode: pageMode,
						framework: InlineCommentFramework.ANNOTATION_PROVIDER,
						commentsDeckSize: numReplies + 1, // +1 for the parent comment
						commentId,
					},
				},
			}).fire();

			if (fg('confluence_frontend_view_inline_comment_exp_parity')) {
				selectInlineComment.debug(
					`Comment ID or page ID was updated. Comment ID: ${commentId}, page ID: ${pageId}`,
				);
			} else {
				experienceTracker.succeed({
					name: VIEW_INLINE_COMMENT_EXPERIENCE,
				});
			}
		}
		// We really only need to depend on these two things and want this to fire when ONLY they change
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pageId, commentId]);

	useEffect(() => {
		if (editCommentQueryId) {
			const isReplyToCommentInChain = commentData?.replies
				?.map((reply: ReplyData) => reply?.id)
				?.includes(editCommentQueryId);
			if (isReplyToCommentInChain) {
				setIsReplyChainCollapsed(false);
			}
			setCommentForEdit(editCommentQueryId);
		}
	}, [editCommentQueryId, commentData]);

	useEffect(() => {
		const isReplyToCommentInChain = commentData?.replies
			?.map((reply: ReplyData) => reply?.id)
			?.includes(replyToCommentId);
		setShouldFocusNewReply(
			Boolean(replyToCommentId) && (isReplyToCommentInChain || replyToCommentId === commentId),
		);
	}, [replyToCommentId, commentId, commentData]);

	useEffect(() => {
		if (shouldFocusNewReply) {
			// for parent comment
			if (commentData && replyToCommentId === commentData.id) {
				handleReplyToComment(commentData?.id, commentData as CommentData);
			} else {
				const replyObj = commentData?.replies?.find(
					(reply: ReplyData) => reply?.id === replyToCommentId,
				);
				if (replyObj && replyObj.parentId) {
					handleReplyToComment(replyObj.parentId, replyObj as ReplyData);
				}
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [shouldFocusNewReply, commentData?.replies]);

	useEffect(() => {
		if (focusedCommentId) {
			const isReplyToCommentInChain = commentData?.replies
				?.map((reply: ReplyData) => reply?.id)
				?.includes(focusedCommentId);
			if (isReplyToCommentInChain) {
				setIsReplyChainCollapsed(false);
			}
		}
	}, [focusedCommentId, commentData]);

	useEffect(() => {
		if (effectiveData && pageInfo) {
			selectInlineComment.debug(
				'Closing create comment as the view component is ready [!apollo_loading]',
			);
			// Close the create comment component when the view component is ready
			handleCloseCreateComment();

			setShouldRender(true);

			selectInlineComment.debug(
				'Data has loaded. Rendering inline comment sidebar... [apollo_loading]',
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [effectiveData, pageInfo]);

	useEffect(() => {
		return () => {
			setShouldRender(false);
			selectInlineComment.debug('Data changed, hiding comment', {
				...(!!commentId && { commentId }),
				pageId,
				selectedAnnotationId: selectedAnnotation.id,
				shouldRender,
			});

			if (fg('confluence_frontend_view_inline_comment_exp_parity')) {
				selectInlineComment.debug(
					`Data has changed. Selected annotation ID: ${selectedAnnotation?.id}, comment ID: ${commentId}, page ID: ${pageId}, shouldRender: ${shouldRender}. Hiding inline comment sidebar (if open)...`,
				);
			}

			if (shouldRender) {
				selectInlineCommentCompoundExperience.abort({
					abortReason: 'Inline comment experience received a new experience while already running',
				});
				if (fg('confluence_frontend_view_inline_comment_exp_parity')) {
					experienceTracker.abort({
						name: VIEW_INLINE_COMMENT_EXPERIENCE,
						reason: `Inline comment component has received new data and is hiding the inline comment sidebar. shouldRender: ${shouldRender}`,
						checkForTimeout: true,
						publishDebugPoints: true,
						attributes: {
							stopPoint: relativeFilePath,
						},
					});
				}
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [commentId, pageId, selectedAnnotation]);

	useEffect(() => {
		// if the user has been shown the resolved comment view, but navigated away to another comment
		// we need to cancel the autodismiss functionality, as it will close this entire view and not just the
		// resolved comment notification
		if (commentId !== resolvedComment?.commentId) {
			clearTimeout(resolveWindowCloseTimeout.current);
			resolveWindowCloseTimeout.current = undefined;
		}
	}, [commentId, resolvedComment, resolveWindowCloseTimeout]);

	const toggleCommentMode = (id = '') => {
		setCommentForEdit(id);
	};

	useEffect(() => {
		const markerRef = (commentData?.location as InlineCommentLocation)?.inlineMarkerRef;
		if (markerRef) {
			setActiveHighlight(markerRef);
		}
	}, [commentData, setActiveHighlight]);

	const handleReplyToComment = (id: string, commentReplyData?: CommentData | ReplyData) => {
		const referencedData = commentReplyData || commentData;
		setShouldFocusNewReply(true);
		setReplyToCommentId({
			parentCommentId: id,
			location: LocationValue.STANDALONE,
			user: {
				displayName: referencedData?.author?.displayName,
				accountId: (referencedData?.author as InlineCommentAuthorKnownUser)?.accountId,
			},
		});
	};

	const deleteCommentInCacheOnUpdate = useCallback(
		(removalCommentId: string) =>
			deleteCommentInCache({
				commentId: removalCommentId,
				onError: (err) => {
					void handleMutationFailure({
						experienceTracker,
						experienceName: DELETE_INLINE_COMMENT_EXPERIENCE,
						error: err,
						commentId: removalCommentId,
					});
				},
			}),
		[deleteCommentInCache, experienceTracker],
	);

	const handleDeleteCommentInRenderer = useCallback(
		(parentId?: string | null, replyCommentId?: string) => {
			const isReply = Boolean(parentId);
			let deleteResult: ActionResult | undefined;
			let mtnVariables: any;

			if (isReply) {
				mtnVariables = {
					variables: {
						commentIdToDelete: replyCommentId,
						deleteFrom: CommentDeletionLocation.LIVE,
					},
				};
			} else {
				try {
					// Call deleteAnnotation(selectedAnnotation) to get step and any other required variables
					deleteResult = deleteOptions?.deleteAnnotation(selectedAnnotation);
				} catch (e) {
					experienceTracker.stopOnError({
						name: DELETE_INLINE_COMMENT_EXPERIENCE,
						error: e,
					});
					return;
				}

				if (!deleteResult) {
					experienceTracker.stopOnError({
						name: DELETE_INLINE_COMMENT_EXPERIENCE,
						error: new Error(`Unable to remove annotation ${selectedAnnotation.id} from document`),
					});
					return;
				}

				mtnVariables = {
					variables: {
						input: {
							commentId,
							step: constructStepForGql(deleteResult.step as AddMarkStep),
						},
					},
				};
			}

			// After deleting it will not be available, so we need to get the inline node types before deleting.
			const inlineNodeTypes = selectedAnnotation
				? getInlineNodeTypes(selectedAnnotation.id)
				: undefined;

			// top-level comment calls `deleteInlineCommentWithStepFn`
			// reply calls `deleteInlineComment`
			const deleteFn = isReply ? deleteInlineCommentFn : deleteInlineCommentWithStepFn;
			const deletedCommentId = isReply ? replyCommentId : commentId;
			deleteFn({
				...mtnVariables,
				update: selectUpdateFunction<MutationResult>(
					expVal('cc_comments_new_comments_data_manager', 'isEnabled', false),
					() => deleteCommentInCacheOnUpdate(deletedCommentId),
					() =>
						isReply
							? apolloCacheCallback(
									'delete',
									{
										pageId,
										annotationId: selectedAnnotation.id,
										contentStatus: (fg('confluence_frontend_content_wrapper')
											? ['DRAFT', 'CURRENT']
											: ['CURRENT', 'DRAFT']) as GraphQLContentStatus[],
									},
									parentId,
									replyCommentId,
								)
							: undefined,
				),
			})
				.then((retVal) => {
					const { data: deleteResultData } = retVal;

					if (!deleteResultData || (!isReply && !deleteResultData.deleteInlineComment)) {
						throw new Error('No data returned from mutation');
					}

					const deleteAnnotationAndCleanUp = (annotationId: string) => {
						// Update storedCommentData if it exists
						if (storedCommentData && clearStoredCommentData) {
							if (!isReply) {
								// Deleting the main comment, clear all data
								clearStoredCommentData();
							} else if (replyCommentId) {
								// Deleting a reply, remove it from the replies array
								const updatedCommentData = {
									...storedCommentData,
									replies: (storedCommentData.replies || []).filter(
										(reply: any) => reply?.id !== replyCommentId,
									),
								};
								setStoredCommentData(updatedCommentData);
							}
						}

						if (!isReply) {
							// We handle this being false above and can't get here if it is
							if (deleteResult) {
								deleteOptions?.onDeleteSuccess(deleteResult.doc, annotationId);
							}

							// Reset the content state on successful deletion on a non-reply
							resetContentChanged();
							onClose && onClose();
							deleteParentComment({
								threadKey: annotationId,
								commentType: CommentType.INLINE,
							});
						} else if (replyCommentId) {
							handleRemovingComments({
								threadKey: annotationId,
								commentId: replyCommentId,
								action: CommentActionType.DELETE_COMMENT,
								commentType: CommentType.INLINE,
							});
						}
						experienceTracker.succeed({
							name: DELETE_INLINE_COMMENT_EXPERIENCE,
						});
					};

					handleDeleteSuccess({
						commentId: isReply ? replyCommentId : commentId,
						annotationId: selectedAnnotation?.id,
						pageId,
						pageMode,
						isReply,
						source: AnalyticsSource.STANDALONE,
						inlineNodeTypes,
						createAnalyticsEvent,
						removeUnresolvedInlineComment,
						onSuccess: deleteAnnotationAndCleanUp,
					});
				})
				.catch((err) => {
					if (fg('confluence_frontend_show_already_deleted_flag')) {
						const parsedError = parseError(err);
						const translatedError = getTranslatedError(
							parsedError.message,
							isReply ? replyCommentId : commentId,
						);

						if (isAlreadyDeletedError(translatedError)) {
							void flags.showWarningFlag({
								title: <FormattedMessage {...i18n.cannotDeleteTitle} />,
								description: <FormattedMessage {...translatedError} />,
							});

							createAnalyticsEvent({
								type: 'sendOperationalEvent',
								data: {
									action: 'alreadyDeleted',
									actionSubject: 'comment',
									actionSubjectId: isReply ? replyCommentId : commentId,
									objectType: 'page',
									objectId: pageId,
									source: 'viewPageScreen',
									attributes: {
										commentType: CommentType.INLINE,
										mode: pageMode,
										framework: InlineCommentFramework.ANNOTATION_PROVIDER,
										isReply,
									},
								},
							}).fire();
						}
					}

					void handleMutationFailure({
						experienceTracker,
						experienceName: DELETE_INLINE_COMMENT_EXPERIENCE,
						error: err,
						commentId: isReply ? replyCommentId : commentId,
					});
				});
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			commentId,
			createAnalyticsEvent,
			deleteOptions,
			deleteCommentInCacheOnUpdate,
			experienceTracker,
			resetContentChanged,
			onClose,
			pageId,
			removeUnresolvedInlineComment,
			selectedAnnotation,
			deleteInlineCommentFn,
			deleteInlineCommentWithStepFn,
			pageMode,
			getInlineNodeTypes,
			handleRemovingComments,
			deleteParentComment,
			flags,
		],
	);

	const handleDeleteCommentInEditorAndLivePages = useCallback(
		(parentId?: string | null, replyCommentId?: string) => {
			const isReply = Boolean(parentId);
			const targetNodeType = getTargetNodeType(selectedAnnotation.id, true);
			const deletedCommentId = isReply ? replyCommentId : commentId;
			// both top-level comments and replies call `deleteInlineComment`
			deleteInlineCommentFn({
				variables: {
					commentIdToDelete: isReply ? replyCommentId : commentId,
					deleteFrom: isLivePage ? CommentDeletionLocation.LIVE : CommentDeletionLocation.EDITOR,
				},
				update: selectUpdateFunction<MutationResult>(
					expVal('cc_comments_new_comments_data_manager', 'isEnabled', false),
					() => deleteCommentInCacheOnUpdate(deletedCommentId),
					() =>
						isReply
							? apolloCacheCallback(
									'delete',
									{
										pageId,
										annotationId: selectedAnnotation.id,
										contentStatus: (fg('confluence_frontend_content_wrapper')
											? ['DRAFT', 'CURRENT']
											: ['CURRENT', 'DRAFT']) as GraphQLContentStatus[],
									},
									parentId,
									replyCommentId,
								)
							: undefined,
				),
			})
				.then(({ data: deleteResultData }) => {
					if (!deleteResultData || (!isReply && !deleteResultData.deleteComment)) {
						throw new Error('No data returned from mutation');
					}

					// Update storedCommentData if it exists
					if (storedCommentData) {
						if (!isReply) {
							// Deleting the main comment, clear all data
							clearStoredCommentData();
						} else if (replyCommentId) {
							// Deleting a reply, remove it from the replies array
							const updatedCommentData = {
								...storedCommentData,
								replies: (storedCommentData.replies || []).filter(
									(reply: any) => reply?.id !== replyCommentId,
								),
							};
							setStoredCommentData(updatedCommentData);
						}
					}

					createAnalyticsEvent({
						type: 'sendTrackEvent',
						data: {
							action: 'deleted',
							actionSubject: 'comment',
							actionSubjectId: isReply ? replyCommentId : commentId,
							objectType: 'page',
							objectId: pageId,
							source: 'editPageScreen',
							attributes: {
								commentType: CommentType.INLINE,
								source: AnalyticsSource.STANDALONE,
								pageMode,
								framework: InlineCommentFramework.ANNOTATION_PROVIDER,
								inlineNodeTypes: selectedAnnotation
									? getInlineNodeTypes(selectedAnnotation.id)
									: undefined,
								isReply,
								targetNodeType,
							},
						},
					}).fire();

					experienceTracker.succeed({
						name: DELETE_INLINE_COMMENT_EXPERIENCE,
					});

					if (!isReply) {
						onDelete?.(selectedAnnotation?.id);
						// Remove the comment from the unresolved list
						removeUnresolvedInlineComment(selectedAnnotation?.id);
						// Reset the content state on successful deletion on a non-reply
						resetContentChanged();
						deleteParentComment({
							threadKey: selectedAnnotation.id,
							commentType: CommentType.INLINE,
						});
					} else if (replyCommentId) {
						handleRemovingComments({
							threadKey: selectedAnnotation.id,
							commentId: replyCommentId,
							action: CommentActionType.DELETE_COMMENT,
							commentType: CommentType.INLINE,
						});
					}
				})
				.catch((err) => {
					if (fg('confluence_frontend_show_already_deleted_flag')) {
						const parsedError = parseError(err);
						const translatedError = getTranslatedError(
							parsedError.message,
							isReply ? replyCommentId : commentId,
						);

						if (isAlreadyDeletedError(translatedError)) {
							void flags.showWarningFlag({
								id: commentId,
								title: <FormattedMessage {...i18n.cannotDeleteTitle} />,
								description: <FormattedMessage {...translatedError} />,
							});

							createAnalyticsEvent({
								type: 'sendOperationalEvent',
								data: {
									action: 'alreadyDeleted',
									actionSubject: 'comment',
									actionSubjectId: isReply ? replyCommentId : commentId,
									objectType: 'page',
									objectId: pageId,
									source: 'editPageScreen',
									attributes: {
										commentType: CommentType.INLINE,
										mode: pageMode,
										framework: InlineCommentFramework.ANNOTATION_PROVIDER,
										isReply,
									},
								},
							}).fire();
						}
					}

					void handleMutationFailure({
						experienceTracker,
						experienceName: DELETE_INLINE_COMMENT_EXPERIENCE,
						error: err,
						commentId: isReply ? replyCommentId : commentId,
					});
				});
		},
		//TODO FIXME: determine the right dep array
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			commentId,
			createAnalyticsEvent,
			deleteInlineCommentFn,
			deleteCommentInCacheOnUpdate,
			experienceTracker,
			pageMode,
			pageId,
			onDelete,
			selectedAnnotation,
			getInlineNodeTypes,
			resetContentChanged,
			isLivePage,
			removeUnresolvedInlineComment,
			handleRemovingComments,
			flags,
		],
	);

	// When handleDeleteComment() is called for a reply, both parentId and replyCommentId are defined
	// When called for a top-level comment, both are undefined
	const handleDeleteComment = useCallback(
		(parentId?: string | null, replyCommentId?: string) => {
			if (!isEditor) {
				handleDeleteCommentInRenderer(parentId, replyCommentId);
			} else {
				handleDeleteCommentInEditorAndLivePages(parentId, replyCommentId);
			}
		},
		[isEditor, handleDeleteCommentInRenderer, handleDeleteCommentInEditorAndLivePages],
	);

	const handleResolveComment = () => {
		const mtnVariables = {
			variables: { commentId, resolved: true },
		};

		resolveInlineCommentFn(mtnVariables)
			.then(() => {
				const annotationId = selectedAnnotation.id;

				// this will be removed when the prop becomes optional
				onResolve(annotationId);

				handleResolveSuccess({
					commentId,
					parentCommentMarkerRef: annotationId,
					pageId,
					pageMode,
					eventEmitter,
					source: AnalyticsSource.STANDALONE,
					removeUnresolvedInlineComment,
					createAnalyticsEvent,
					getInlineNodeTypes,
					commentType: CommentType.INLINE,
				});

				resolveParentComment({
					threadKey: annotationId,
					commentType: CommentType.INLINE,
				});

				setOrderedActiveAnnotationIdList(
					orderedActiveAnnotationIdList
						.map((status) => status.threadKey)
						.filter((id) => id !== annotationId),
				);

				experienceTracker.succeed({
					name: RESOLVE_INLINE_COMMENT_EXPERIENCE,
				});

				if (isEditor) {
					handleOnClose();
				} else {
					onClose && onClose();
				}

				if (allowComments) {
					showResolvedCommentFlag({
						commentId: commentId ?? '',
						experienceTracker,
						flags,
						mode: pageMode,
						annotationId,
						formatMessage,
						showCommentsPanel,
						setCurrentView,
						isCommentsPanelShown,
						handleReopenComment: createHandleReopenComment({
							reopenInlineCommentFn,
							commentId: commentId ?? '',
							annotationId,
							reopenParentComment,
							setOrderedActiveAnnotationIdList,
							orderedActiveAnnotationIdList,
							highlightReopenedComment,
							isEditor: isEditor ?? false,
							eventEmitter,
							setActiveHighlight,
							experienceTracker,
						}),
						setCurrentlySelectedThreadKey,
					});
				}

				// Show the resolved comment window if not in editor
				if (!isEditor) {
					if (commentId) {
						setResolvedComment({
							commentId,
							inlineMarkerRef: annotationId,
						});
					}

					// If the comment we just resolved is the same as the one we're currently viewing, set a timeout
					// to auto-close the container as it should be showing the resolved comment notification view
					if (resolvedComment?.commentId === commentData?.id) {
						resolveWindowCloseTimeout.current = setTimeout(() => {
							onClose && onClose();
						}, 7500);
					}
				}
			})
			.catch((err) => {
				void handleMutationFailure({
					experienceTracker,
					experienceName: RESOLVE_INLINE_COMMENT_EXPERIENCE,
					error: err,
				});
			});
	};

	const onCancelComment = () => {
		setReplyToCommentId();
		setShouldFocusNewReply(false);
	};

	const handleCreateReplyUnified = async (adf: object, onSuccess: () => void) => {
		const parentCommentId = commentId ?? '';
		// eslint-disable-next-line confluence-feature-gating/inline-usage
		const variables = {
			input: {
				containerId: pageId,
				parentCommentId,
				commentBody: {
					value: JSON.stringify(adf),
					representationFormat: 'ATLAS_DOC_FORMAT' as ContentRepresentation,
				},
				createdFrom: isLivePage
					? CommentCreationLocation.LIVE
					: isEditor
						? CommentCreationLocation.EDITOR
						: CommentCreationLocation.RENDERER,
			},
			pageId,
			includeReactions: false,
		};

		const queryVariables = {
			annotationId: selectedAnnotation?.id,
			pageId,
			contentStatus: ['DRAFT', 'CURRENT'] as GraphQLContentStatus[],
		};

		startEditorSubExperience({
			name: REPLY_TO_INLINE_COMMENT_PUBLISH_EXPERIENCE,
			attributes: {
				parentCommentId,
			},
		});

		return createReplyFn({
			variables,
			update: apolloCacheCallback('create', queryVariables, parentCommentId),
		})
			.then(({ data }: any) => {
				const replyInlineComment = data.replyInlineComment;
				if (!replyInlineComment?.reactionsSummary) {
					replyInlineComment.reactionsSummary = defaultReactionsSummary;
				}
				const reply: ReplyData = {
					...replyInlineComment,
					isUnread: false,
				};

				// Update storedCommentData if it exists by adding the new reply
				if (storedCommentData && setStoredCommentData) {
					const updatedCommentData = {
						...storedCommentData,
						replies: [...(storedCommentData.replies || []), replyInlineComment],
					};
					setStoredCommentData(updatedCommentData);
				}

				addReplyToCommentThread(selectedAnnotation.id, reply, CommentType.INLINE);
				// reset the replyToCommentUser if any
				replyToCommentUser && setReplyToCommentId();
				handleCreateReplySuccess({
					onSuccess,
					editCommentQueryId,
					setCommentForEdit,
					data,
					createAnalyticsEvent,
					pageId,
					pageType,
					analyticsSource: AnalyticsSource.STANDALONE,
					commentId,
					pageMode,
					selectedAnnotation,
					getInlineNodeTypes: undefined,
					experienceTracker,
					isEditor: !!isEditor,
				});
			})
			.catch((error: any) => {
				return handleMutationFailure({
					experienceTracker,
					experienceName: REPLY_TO_INLINE_COMMENT_PUBLISH_EXPERIENCE,
					error,
					commentId,
					shouldReturnError: true,
					shouldAbortForExpectedError: true,
					compoundExperienceName: REPLY_TO_INLINE_COMMENT_EXPERIENCE,
				});
			});
	};

	const updateReactionsCacheFnLegacy = useCallback(
		(
			emojiId: string,
			actionType: 'add' | 'delete',
			objectId: string,
			containerId?: string,
			cache?: DataProxy,
		) => {
			const queryVariables = {
				pageId: containerId!,
				annotationId: selectedAnnotation?.id,
				contentStatus: [isArchived ? 'ARCHIVED' : 'DRAFT', 'CURRENT'] as GraphQLContentStatus[],
			};

			try {
				const response: InlineCommentQueryType | null | undefined = cache?.readQuery<
					InlineCommentQueryType,
					InlineCommentQueryVariables
				>({
					query: InlineCommentQuery,
					variables: queryVariables,
				});

				if (!response?.comments?.nodes?.[0]) {
					return;
				}

				const comment = response.comments.nodes[0];
				let cacheReactionsSummary;
				let reply;

				// If the comment matches the comment ID, use it's reactionsSummary
				if (comment.id === objectId) {
					cacheReactionsSummary = comment.reactionsSummary
						? { ...comment.reactionsSummary }
						: defaultReactionsSummary;
				} else {
					// Otherwise, find the reply that matches
					reply = comment.replies.find((r) => r?.id === objectId);
					if (reply) {
						cacheReactionsSummary = reply.reactionsSummary
							? { ...reply.reactionsSummary }
							: defaultReactionsSummary;
					}
				}

				if (!cacheReactionsSummary) {
					return;
				}

				const reactionsAri = cacheReactionsSummary.ari || '';
				const reactionsContainerAri = cacheReactionsSummary.containerAri || '';
				let reactionsCount = cacheReactionsSummary.reactionsCount || 0;
				const updatedReactionsSummary = { ...cacheReactionsSummary };
				const updatedReactionsNodes = [...(cacheReactionsSummary.reactionsSummaryForEmoji || [])];

				if (actionType === 'add') {
					const reaction = updatedReactionsNodes.find((item) => item?.emojiId === emojiId);
					// If reaction exists, update its attributes
					if (reaction && !reaction.reacted) {
						reaction.count++;
						reaction.reacted = true;
					} else {
						// Otherwise, create a new reaction
						const newReaction = {
							emojiId,
							count: 1,
							reacted: true,
							id: `${reactionsContainerAri}|${reactionsAri}|${emojiId}`,
							__typename: 'ReactionsSummaryForEmoji' as const,
						};
						updatedReactionsNodes.push(newReaction);
					}
					++reactionsCount;
				} else if (actionType === 'delete') {
					const reactionIdx = updatedReactionsNodes.findIndex((item) => item?.emojiId === emojiId);
					// If reaction exists, update its attributes
					if (reactionIdx !== -1) {
						const reaction = updatedReactionsNodes[reactionIdx];
						// If we have more than one reaction, remove our user from it and decrement the count
						if (reaction && reaction.count > 1) {
							reaction.count--;
							reaction.reacted = false;
						} else {
							// Otherwise, remove the reaction entirely
							updatedReactionsNodes.splice(reactionIdx, 1);
						}
						--reactionsCount;
					}
				}

				updatedReactionsSummary.reactionsSummaryForEmoji = updatedReactionsNodes;
				updatedReactionsSummary.reactionsCount = reactionsCount ?? 0;

				// Create a deep copy of the comment to ensure Apollo detects the change
				const updatedComment = {
					...comment,
					replies: comment.replies.map((r) => {
						if (r?.id === objectId) {
							return {
								...r,
								reactionsSummary: updatedReactionsSummary,
							};
						}
						return r;
					}),
				};

				// Update the comment's reactionsSummary if it's the target comment
				if (comment.id === objectId) {
					// @ts-ignore reactionsCount will always be defined, so the type comparison is wrong
					updatedComment.reactionsSummary = updatedReactionsSummary;
				}

				const newData = {
					comments: {
						nodes: [updatedComment],
						__typename: 'PaginatedCommentList',
					},
				};

				cache?.writeQuery({
					query: InlineCommentQuery,
					variables: queryVariables,
					data: newData,
				});
			} catch (err) {
				logger.error`An error occurred when updating reactions cache for ${actionType} - ${err}`;
			}
		},
		[selectedAnnotation, isArchived],
	);

	const updateReactionsCacheFn = useCallback(
		(
			emojiId: string,
			actionType: 'add' | 'delete',
			objectId: string,
			containerId?: string,
			cache?: DataProxy,
		) => {
			if (fg('confluence_frontend_content_wrapper')) {
				// Call the GA function
				updateReactionsCacheCallbackForGA(
					emojiId,
					actionType,
					objectId,
					pageId,
					selectedAnnotation,
					isArchived ?? false,
					cache,
				);

				// Also update storedCommentData if it exists
				if (storedCommentData && setStoredCommentData) {
					const updatedCommentData = { ...storedCommentData };

					// Find if this is the main comment or a reply
					if (storedCommentData.id === objectId) {
						// Update main comment reactions
						const currentReactions =
							storedCommentData.reactionsSummary?.reactionsSummaryForEmoji || [];
						const updatedReactions = [...currentReactions];
						let reactionsCount = storedCommentData.reactionsSummary?.reactionsCount || 0;

						if (actionType === 'add') {
							const existingReaction = updatedReactions.find((r: any) => r?.emojiId === emojiId);
							if (existingReaction && !existingReaction.reacted) {
								existingReaction.count++;
								existingReaction.reacted = true;
							} else if (!existingReaction) {
								updatedReactions.push({
									emojiId,
									count: 1,
									reacted: true,
									id: `${storedCommentData.reactionsSummary?.containerAri}|${storedCommentData.reactionsSummary?.ari}|${emojiId}`,
									__typename: 'ReactionsSummaryForEmoji',
								});
							}
							reactionsCount++;
						} else if (actionType === 'delete') {
							const reactionIdx = updatedReactions.findIndex((r: any) => r?.emojiId === emojiId);
							if (reactionIdx !== -1) {
								const reaction = updatedReactions[reactionIdx];
								if (reaction && reaction.count > 1) {
									reaction.count--;
									reaction.reacted = false;
								} else {
									updatedReactions.splice(reactionIdx, 1);
								}
								reactionsCount--;
							}
						}

						updatedCommentData.reactionsSummary = {
							...storedCommentData.reactionsSummary,
							reactionsSummaryForEmoji: updatedReactions,
							reactionsCount,
							ari: storedCommentData.reactionsSummary?.ari || '',
						} as typeof storedCommentData.reactionsSummary;
					} else {
						// Update reply reactions
						updatedCommentData.replies = (storedCommentData.replies || []).map((reply: any) => {
							if (reply?.id === objectId) {
								const currentReactions = reply.reactionsSummary?.reactionsSummaryForEmoji || [];
								const updatedReactions = [...currentReactions];
								let reactionsCount = reply.reactionsSummary?.reactionsCount || 0;

								if (actionType === 'add') {
									const existingReaction = updatedReactions.find(
										(r: any) => r?.emojiId === emojiId,
									);
									if (existingReaction && !existingReaction.reacted) {
										existingReaction.count++;
										existingReaction.reacted = true;
									} else if (!existingReaction) {
										updatedReactions.push({
											emojiId,
											count: 1,
											reacted: true,
											id: `${reply.reactionsSummary?.containerAri}|${reply.reactionsSummary?.ari}|${emojiId}`,
											__typename: 'ReactionsSummaryForEmoji',
										});
									}
									reactionsCount++;
								} else if (actionType === 'delete') {
									const reactionIdx = updatedReactions.findIndex(
										(r: any) => r?.emojiId === emojiId,
									);
									if (reactionIdx !== -1) {
										const reaction = updatedReactions[reactionIdx];
										if (reaction && reaction.count > 1) {
											reaction.count--;
											reaction.reacted = false;
										} else {
											updatedReactions.splice(reactionIdx, 1);
										}
										reactionsCount--;
									}
								}

								return {
									...reply,
									reactionsSummary: {
										...reply.reactionsSummary,
										reactionsSummaryForEmoji: updatedReactions,
										reactionsCount,
										ari: reply.reactionsSummary?.ari || '',
									} as typeof reply.reactionsSummary,
								};
							}
							return reply;
						});
					}

					setStoredCommentData(updatedCommentData);
				}
			} else {
				updateReactionsCacheFnLegacy(emojiId, actionType, objectId, containerId, cache);
			}
		},
		[
			pageId,
			selectedAnnotation,
			isArchived,
			updateReactionsCacheFnLegacy,
			storedCommentData,
			setStoredCommentData,
		],
	);

	const renderReplies = useCallback(
		(shouldCollapseReplies: boolean) => {
			const replies = effectiveData?.comments?.nodes?.[0]?.replies || [];
			const visibleReplies = shouldCollapseReplies ? replies.slice(-1) : replies;

			return visibleReplies.map((reply: ReplyData) => {
				if (!reply) {
					return <></>;
				}
				const {
					id,
					permissions: replyPermissions,
					body,
					reactionsSummary: replyReactionsSummary,
					createdAtNonLocalized: replyCreatedAtNonLocalized,
				} = reply;

				const matchingRef = unreadCommentRefs.find((refObj) => refObj.id === id)?.ref;
				if (id === editCommentId) {
					return (
						<EditComment
							key={id}
							commentId={id}
							annotationId={selectedAnnotation.id}
							displayName={reply.author?.displayName ?? formatMessage(commentsi18n.anonymous)}
							avatarUrl={reply.author?.profilePicture?.path ?? ''}
							pageId={pageId}
							pageType={pageType}
							spaceId={spaceId}
							content={body?.value}
							displayCommentInViewMode={toggleCommentMode}
							mode={isEditor ? 'edit' : 'view'}
							isReply
							hasMediaUploadPermissions={hasMediaUploadPermissions}
							getInlineNodeTypes={getInlineNodeTypes}
							commentType={CommentType.INLINE}
							date={reply.version?.when ?? ''}
							versionDate={reply.version?.when ?? ''}
						/>
					);
				} else {
					return (
						<CommentBody
							key={id}
							userId={(reply.author as InlineCommentAuthorKnownUser)?.accountId ?? ''}
							displayName={reply.author?.displayName}
							avatarUrl={reply.author?.profilePicture?.path}
							date={reply.version?.when ?? ''}
							dateUrl={
								isEditor && !isLivePage
									? (reply.links.editui ?? undefined)
									: (reply.links.webui ?? undefined)
							}
							createdAt={reply.createdAt?.value ?? ''}
							createdAtNonLocalized={replyCreatedAtNonLocalized ?? ''}
							versionDate={reply.version?.when ?? ''}
							versionByDisplayName={reply.version?.by?.displayName}
							commentId={id}
							pageId={pageId}
							pageType={pageType}
							isReply
							isEdited={(reply.version?.number || 0) > 1}
							parentCommentId={reply.parentId}
							permissions={replyPermissions}
							editComment={() => toggleCommentMode(id)}
							deleteComment={() => handleDeleteComment(reply.parentId, id)}
							content={reply.body?.value}
							isCommentActive
							mode={isEditor ? 'edit' : 'view'}
							permissionType={reply.author?.permissionType ?? undefined}
							supportedActions={['edit', 'delete', 'resolve']}
							isFocused={focusedCommentId === id}
							isUnread={
								fg('confluence_frontend_unread_comments_fixes')
									? unreadCommentIDSet.has(id)
									: currentUnreadIds.has(id)
							}
							onRendered={() => {
								if (fg('confluence-fe-comments-deep-link-behavior')) {
									return;
								}
								const targetCommentId = focusedCommentId || replyToCommentId;
								if (sidebarOffset > 0 && targetCommentId) {
									handleScrollToElement({
										commentId: id,
										targetCommentId,
										isEditor: Boolean(isEditor),
										useInstantScroll: true,
									});
								}
							}}
							unreadCommentRef={matchingRef}
							commentType={CommentType.INLINE}
							inheritedReactionsData={replyReactionsSummary}
							reactionsCacheUpdateFn={updateReactionsCacheFn}
							canAddComments={canAddComments}
							isOffline={isOffline}
							isFabricPage
						/>
					);
				}
			});
		},
		[
			effectiveData,
			editCommentId,
			pageType,
			pageId,
			spaceId,
			selectedAnnotation,
			isEditor,
			focusedCommentId,
			replyToCommentId,
			handleDeleteComment,
			isLivePage,
			sidebarOffset,
			currentUnreadIds,
			unreadCommentRefs,
			hasMediaUploadPermissions,
			getInlineNodeTypes,
			updateReactionsCacheFn,
			isOffline,
			canAddComments,
			formatMessage,
			unreadCommentIDSet,
		],
	);

	const handleOnClose = useCallback(() => {
		const isReply = editCommentId === '';

		experienceTracker.abort({
			name: isReply ? REPLY_TO_INLINE_COMMENT_EXPERIENCE : EDIT_INLINE_COMMENT_EXPERIENCE,
			reason: 'comment discarded by user',
		});

		// Clearing reply draft is handled in the comment editor
		clearCommentDraft(isEditor ? 'edit-inline' : 'inline', isReply ? 'reply' : 'edit');
		setIsReplyChainCollapsed(true);
		setShouldFocusNewReply(false);

		onClose && onClose();

		setActiveHighlight(null);
	}, [experienceTracker, editCommentId, isEditor, onClose, setActiveHighlight]);

	const highlightReopenedComment = useCallback(
		(annotationId: string) => {
			// Emit the event to re-highlight the mark
			eventEmitter.emit(AnnotationUpdateEvent.SET_ANNOTATION_STATE, {
				[annotationId]: {
					id: annotationId,
					annotationType: AnnotationTypes.INLINE_COMMENT,
					state: AnnotationMarkStates.ACTIVE,
				},
			});

			addUnresolvedInlineComment(annotationId, pageMode);
		},
		[addUnresolvedInlineComment, eventEmitter, pageMode],
	);

	const navigationOptions = useMemo(
		() => ({
			// We don't want to show the comment counts for the resolved comment view, and we need to make sure
			// that we're not actually viewing a different comment to the resolved comment
			disableCommentCount:
				Boolean(resolvedComment?.commentId === commentData?.id) ||
				fg('confluence-frontend-comments-panel'),
			commentData: commentData as any,
			onNavigationClick,
		}),
		[resolvedComment, commentData, onNavigationClick],
	);

	useEffect(() => {
		if (!selectedAnnotation.id) {
			return;
		}

		if (shouldRender === false) {
			// The UI has not started rendering yet, so we don't want to check if it's visible.
			selectInlineComment.debug(
				`InlineComment has been rendered, but is returning null due to shouldRender. Selected annotation ID: ${selectedAnnotation?.id}`,
			);

			return;
		}

		const initialTaskId = experienceTracker.getExperienceState(
			SELECT_INLINE_COMMENT_EXPERIENCE,
		)?.taskId;

		// Add a guard for rapid changes
		let isCancelled = false;

		const timeoutId = setTimeout(() => {
			// There are multiple apis and effects involved in swapping from the create comment
			// ui to the view comment ui.
			// This works around this by only marking the experience as complete after
			// a short timeout + animation frame.

			if (isCancelled && fg('confluence_frontend_view_inline_is_cancelled')) return;

			requestAnimationFrame(() => {
				if (isCancelled && fg('confluence_frontend_view_inline_is_cancelled')) return;

				const currentTaskId = experienceTracker.getExperienceState(
					SELECT_INLINE_COMMENT_EXPERIENCE,
				)?.taskId;

				if (!currentTaskId) {
					// Note -- this should not do anything (as debug points are only added to active experiences)
					selectInlineComment.debug('No current task ID');
					return;
				}

				if (initialTaskId !== currentTaskId) {
					// The experience has been restarted or has otherwise ended.
					if (fg('confluence_frontend_view_inline_comment_exp_parity')) {
						experienceTracker.attachDebugPoint({
							name: VIEW_INLINE_COMMENT_EXPERIENCE,
							message: `Experience has been restarted or has otherwise ended. initialTaskId: ${initialTaskId}, currentTaskId: ${currentTaskId}`,
						});
					}
					return;
				}

				if (lastSelected.current !== selectedAnnotation.id) {
					// The selected annotation has changed, this should have led to an earlier abort
					// (as an overlapping experience is started)
					selectInlineComment.debug(
						`View inline comment assumed aborted due to selection change. lastSelected.current: ${lastSelected?.current}, selectedAnnotation.id: ${selectedAnnotation?.id}`,
					);
					return;
				}

				if (!annotationElementRef.current) {
					// We'd expect the select annotation experience to fail
					// if no annotation is added to the document
					// Assume this experience has aborted (or it will timeout)
					selectInlineComment.debug(
						'Show inline comment assumed aborted by deselection or selection change. This message indicate an abort failed and something has gone wrong.',
					);
					selectInlineCommentCompoundExperience.abort({
						abortReason: 'No annotation element found for the inline comment ui to attach to.',
					});
					if (fg('confluence_frontend_view_inline_comment_exp_parity')) {
						experienceTracker.abort({
							name: VIEW_INLINE_COMMENT_EXPERIENCE,
							reason: 'No annotation element found for the inline comment component to attach to.',
							checkForTimeout: true,
							publishDebugPoints: true,
							attributes: {
								stopPoint: relativeFilePath,
							},
						});
					}
					if (fg('confluence_frontend_null_annotation_flag')) {
						void flags.showWarningFlag({
							title: <FormattedMessage {...i18n.commentLoadErrorTitle} />,
							description: <FormattedMessage {...i18n.commentLoadError} />,
						});
					}
					return;
				}

				if (!sidebarEl.current) {
					selectInlineCommentCompoundExperience.showComment.fail(
						new Error('Document missing inline comment ui'),
					);
					if (shouldRender && fg('confluence_frontend_view_inline_comment_exp_parity')) {
						experienceTracker.fail({
							name: VIEW_INLINE_COMMENT_EXPERIENCE,
							error: new Error('Document is missing inline comment sidebar element'),
							attributes: {
								stopPoint: relativeFilePath,
							},
						});
					}
					return;
				}

				const commentUIVerticalDistanceFromAnnotation = Math.abs(
					annotationElementRef.current.getBoundingClientRect().top -
						sidebarEl.current.getBoundingClientRect?.().top,
				);

				// The 40 pixel allowance has been arbitrarily chosen to allow for some wiggle room
				if (commentUIVerticalDistanceFromAnnotation > 40) {
					const isVisible = sidebarEl.current.checkVisibility();
					if (isVisible) {
						// if it's fully visible, but misaligned, we don't treat this as a failure
						selectInlineComment.debug('View Inline Comment UI too far from annotation', {
							misalignedBy: commentUIVerticalDistanceFromAnnotation,
						});
					} else {
						selectInlineCommentCompoundExperience.showComment.fail(
							new Error('View Created Comment UI too far from annotation'),
							{ misalignedBy: commentUIVerticalDistanceFromAnnotation },
						);

						if (fg('confluence_frontend_view_inline_comment_exp_parity')) {
							experienceTracker.fail({
								name: VIEW_INLINE_COMMENT_EXPERIENCE,
								error: new Error(
									'View inline comment sidebar was rendered too far from annotation',
								),
								attributes: {
									misalignedBy: commentUIVerticalDistanceFromAnnotation,
									stopPoint: relativeFilePath,
								},
							});
						}
						return;
					}
				}

				if (fg('confluence_frontend_view_inline_comment_exp_parity')) {
					experienceTracker.succeed({
						name: VIEW_INLINE_COMMENT_EXPERIENCE,
						publishDebugPoints: true,
						attributes: {
							stopPoint: relativeFilePath,
						},
					});
				}

				selectInlineCommentCompoundExperience.showComment.complete(
					'Inline Comment panel comment shown',
				);
			});
		}, 10);

		return () => {
			isCancelled = true;
			clearTimeout(timeoutId);
			setShouldRender(false);

			const currentTaskId = experienceTracker.getExperienceState(
				SELECT_INLINE_COMMENT_EXPERIENCE,
			)?.taskId;

			if (!currentTaskId) {
				// There is no running experience, no need to abort.
				return;
			}

			if (initialTaskId !== currentTaskId) {
				// The experience has been restarted or has otherwise ended.
				return;
			}

			// If a show inline comment experience is still running while this component is in the process of being
			// unmounted, we abort it rather than fail it.
			selectInlineCommentCompoundExperience.showComment.abort({
				abortReason: 'InlineComment component unmounted',
			});

			if (fg('confluence_frontend_view_inline_comment_exp_parity')) {
				experienceTracker.abort({
					name: VIEW_INLINE_COMMENT_EXPERIENCE,
					reason: 'Inline comment component unmounted',
					checkForTimeout: true,
					publishDebugPoints: true,
					attributes: {
						stopPoint: relativeFilePath,
					},
				});
			}
		};
	}, [selectedAnnotation.id, shouldRender, createAnalyticsEvent, experienceTracker, flags]);

	const [isStandalonehovered, setisStandalonehovered] = useState<boolean>(false);
	const [hoveredCommentId, setHoveredCommentId] = useState<string | undefined>(undefined);
	const [commentIsFocused, setCommentIsFocused] = useState<boolean>(false);

	const handleMouseEnter = useCallback((id?: string) => {
		if (!fg('confluence_frontend_content_wrapper')) {
			return;
		}
		setisStandalonehovered(true);
		if (id) {
			setHoveredCommentId(id);
		}
	}, []);

	const handleMouseLeave = useCallback(() => {
		if (!fg('confluence_frontend_content_wrapper')) {
			return;
		}
		setisStandalonehovered(false);
		setHoveredCommentId(undefined);
	}, []);

	const handleFocus = useCallback(
		(event: React.FocusEvent) => {
			setCommentIsFocused(true);
			event.stopPropagation();
		},
		[setCommentIsFocused],
	);

	const handleBlur = useCallback(
		(event: React.FocusEvent) => {
			if (!event.relatedTarget || !event.currentTarget.contains(event.relatedTarget as Node)) {
				setCommentIsFocused(false);
			}
		},
		[setCommentIsFocused],
	);

	if (!shouldRender) {
		return null;
	}

	const renderComment = () => {
		const matchingRef = unreadCommentRefs.find((refObj) => refObj.id === commentId)?.ref;
		if (commentId === editCommentId) {
			return (
				<EditComment
					pageId={pageId}
					date={when ?? ''}
					createdAtNonLocalized={createdAtNonLocalized ?? ''}
					pageType={pageType}
					annotationId={selectedAnnotation.id}
					commentId={commentId}
					spaceId={spaceId}
					content={content ?? ''}
					isReply={false}
					displayCommentInViewMode={toggleCommentMode}
					avatarUrl={topCommentUserAvatar ?? ''}
					displayName={topCommentDisplayName}
					dateUrl={commentUrl ?? ''}
					versionDate={when ?? ''}
					isEdited={isEdited}
					resolveProperties={resolveProperties}
					mode={isEditor ? 'edit' : 'view'}
					hasMediaUploadPermissions={hasMediaUploadPermissions}
					getInlineNodeTypes={getInlineNodeTypes}
					commentType={CommentType.INLINE}
					isStandaloneComment
				/>
			);
		} else {
			const getDeleteFn = () => {
				if (!isLivePage && isEditor) {
					return undefined;
				}

				return handleDeleteComment(undefined);
			};

			return (
				<Fragment>
					<CommentBody
						userId={topCommentUserId}
						avatarUrl={topCommentUserAvatar}
						displayName={topCommentDisplayName}
						date={when ?? ''}
						dateUrl={commentUrl ?? ''}
						createdAt={createdAt ?? ''}
						createdAtNonLocalized={createdAtNonLocalized ?? ''}
						versionDate={when ?? ''}
						versionByDisplayName={versionByDisplayName}
						pageId={pageId}
						pageType={pageType}
						commentId={commentId ?? ''}
						isReply={false}
						isEdited={isEdited}
						permissions={permissions as CommentPermissions}
						deleteComment={getDeleteFn}
						editComment={() => toggleCommentMode(commentId)}
						content={content ?? ''}
						isCommentActive
						mode={isEditor ? 'edit' : 'view'}
						onClose={onClose}
						permissionType={permissionType ?? undefined}
						resolveComment={handleResolveComment}
						supportedActions={supportedTopLevelActions}
						isUnread={
							fg('confluence_frontend_unread_comments_fixes')
								? unreadCommentIDSet.has(commentId ?? '')
								: currentUnreadIds.has(commentId ?? '')
						}
						numReplies={numReplies}
						unreadCommentRef={matchingRef}
						commentType={CommentType.INLINE}
						inheritedReactionsData={reactionsSummary}
						reactionsCacheUpdateFn={updateReactionsCacheFn}
						isStandalonehovered={isStandalonehovered}
						isHovered={
							hoveredCommentId === commentId ||
							(commentIsFocused && fg('confluence_comments_hover_state_on_focused_comment'))
						}
						replyToComment={handleReplyToComment}
						hideBranchingStyle={!commentData?.replies || commentData?.replies?.length === 0}
						resolveProperties={resolveProperties}
						isStandaloneComment
						canAddComments={canAddComments}
						isOffline={isOffline}
						isFabricPage
						onRendered={() => {
							if (fg('confluence_frontend_view_inline_comment_exp_parity')) {
								experienceTracker.attachDebugPoint({
									name: VIEW_INLINE_COMMENT_EXPERIENCE,
									message: 'Inline comment body rendered',
								});
							}
						}}
					/>
					{!fg('confluence_frontend_content_wrapper') && (
						<ReopenedMessage resolvedUser={resolvedUser ?? undefined} resolvedTime={resolvedTime} />
					)}
					<PageSegmentLoadEnd key={`stop-${commentId}`} metric={RENDER_METRIC} />
				</Fragment>
			);
		}
	};

	annotationElementRef.current = document.getElementById(selectedAnnotation.id);

	// Handle case where comment has been deleted
	if (!annotationElementRef.current) {
		if (fg('confluence_frontend_view_inline_comment_exp_parity')) {
			selectInlineComment.debug(
				'There is no annotation element element, so not rendering InlineComment',
			);
		}
		return null;
	}

	const isCommentAuthor = topCommentUserId === currentUserId;
	const isFirstComment = commentThreadLength === 1;

	return (
		<CommentSidebar
			nudgeRef={nudgeRef}
			pageId={pageId}
			annotationElement={annotationElementRef.current}
			navigationOptions={navigationOptions as CommentNavigationOptions}
			onClose={
				isEditor
					? handleOnClose
					: () => {
							onClose && onClose();
							setActiveHighlight(null);
						}
			}
			isEditor={isEditor}
			isViewCommentMode
			sidebarOffset={sidebarOffset}
			commentId={commentId}
			isOpeningMediaCommentFromToolbar={isOpeningMediaCommentFromToolbar}
			sidebarEl={sidebarEl}
			annotationId={selectedAnnotation?.id}
			inlineCommentRef={inlineCommentRef}
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning
			{...(fg('confluence-fe-comment-temp-max-height-fix') &&
			fg('platform_editor_fix_popup_no_show_inline_comment')
				? { setPopupsScrollableElement }
				: {})}
			isSticky={isSticky}
			isFabricPage
			authorName={topCommentDisplayName}
		>
			{markCommentReadError && <ErrorDisplay error={markCommentReadError} />}
			<Fragment>
				<Box
					testId="inline-comment-container"
					onMouseEnter={() => handleMouseEnter(commentId)}
					onMouseLeave={handleMouseLeave}
					onBlur={handleBlur}
					onFocus={handleFocus}
					tabIndex={0}
					aria-label={formatMessage(i18n.commentAriaLabel, {
						displayName: topCommentDisplayName,
						timestamp: formatTimestampForAriaLabel({
							intl,
							dateString: commentData?.createdAtNonLocalized,
							commentStatus,
							resolveProperties:
								commentData?.location?.inlineResolveProperties ||
								commentData?.location?.commentResolveProperties,
						}),
					})}
				>
					{renderComment()}
				</Box>
				{!fg('confluence_frontend_content_wrapper') && commentData?.replies?.length > 0 && (
					<ReplyListContainer data-testid="inline-comment-reply-container">
						{shouldCollapseReplies && (
							<CollapsedReplies
								numCollapsedReplies={numReplies - 1}
								onClick={() => setIsReplyChainCollapsed(false)}
							/>
						)}
						{renderReplies(Boolean(shouldCollapseReplies))}
					</ReplyListContainer>
				)}
				{commentData?.replies?.length > 0 && fg('confluence_frontend_content_wrapper') && (
					<CommentReplies
						parentComment={normalizeCommentData({
							comment: commentData as unknown as ActiveInlineCommentNodesType,
							readCommentsListState,
							currentUserId: currentUserId ?? '',
							pageMode,
							isOpen: true,
							type: CommentType.INLINE,
						})}
						pageInfo={pageInfo}
						pageMode={pageMode}
						parentCommentId={commentData.id}
						handleMouseEnter={handleMouseEnter}
						handleMouseLeave={handleMouseLeave}
						canAddComments={canAddComments}
						isCurrentSelectedComment={false}
						hoveredCommentId={hoveredCommentId ?? ''}
						isStandaloneComment
						editCommentQueryId={editCommentQueryId}
						selectedAnnotation={selectedAnnotation}
						key={`standalone-comment-replies-${commentData.id}`} // Needed to remount this component when we change which comment we're viewing
						deleteStandaloneComment={handleDeleteComment}
						replyToComment={handleReplyToComment}
						updateReactionsCacheFn={updateReactionsCacheFn}
						// eslint-disable-next-line confluence-feature-gating/no-preconditioning
						{...(popupsScrollableElement &&
						fg('confluence-fe-comment-temp-max-height-fix') &&
						fg('platform_editor_fix_popup_no_show_inline_comment')
							? {
									popupsScrollableElement,
									popupsMountPoint:
										document.getElementById('editor-comments-sidebar') || document.body,
								}
							: {})}
						onRendered={() => {
							if (fg('confluence-fe-comments-deep-link-behavior')) {
								return;
							}
							const targetCommentId = focusedCommentId || replyToCommentId;
							if (sidebarOffset > 0 && targetCommentId && commentData?.id) {
								handleScrollToElement({
									commentId: commentData.id,
									targetCommentId,
									isEditor: Boolean(isEditor),
									useInstantScroll: true,
								});
							}
						}}
					/>
				)}

				{!isArchived && canAddComments && (
					<NewReplyContainer
						mode={isEditor ? 'edit' : 'view'}
						data-cy="editor-reply-container"
						data-testid="inline-comment-new-reply-container"
						ref={commentContainerElementRef}
						isFabricPage
					>
						{!fg('confluence_frontend_content_wrapper') && (
							<CommentAuthor
								commentMode="reply"
								userId={currentUserId}
								size="small"
								permissionType={permissionType ?? undefined}
							/>
						)}
						<CommentEditor
							pageId={pageId}
							pageType={pageType}
							onCancelComment={onCancelComment}
							content={
								fg('confluence_frontend_content_wrapper')
									? getAdfForQuickReplyMention(replyToCommentUser)
									: undefined
							}
							appearance="chromeless"
							EditorComponent={ChromelessEditor}
							onSaveComment={handleCreateReplyUnified}
							onContentChange={onChange}
							onEditorReady={() => {
								experienceTracker.succeed({
									name: REPLY_TO_INLINE_COMMENT_LOAD_EXPERIENCE,
								});
							}}
							commentMode="reply"
							commentType={isEditor ? 'edit-inline' : 'inline'}
							spaceId={spaceId}
							showCancelButton
							useNewWarningModal
							hideWatchCheckbox
							expandEditor={shouldFocusNewReply}
							pageMode={isEditor ? 'edit' : 'view'}
							hasMediaUploadPermissions={hasMediaUploadPermissions}
							topLevelCommentId={commentId}
							shouldWarnOnInternalNavigation
							commentContainerElementRef={commentContainerElementRef}
							isStandaloneComment={fg('confluence_frontend_content_wrapper')}
							// Needed to remount this component when we change which comment we're viewing
							key={`comment-editor-${commentId}`}
							// eslint-disable-next-line confluence-feature-gating/no-preconditioning
							{...(popupsScrollableElement &&
							fg('confluence-fe-comment-temp-max-height-fix') &&
							fg('platform_editor_fix_popup_no_show_inline_comment')
								? {
										popupsScrollableElement,
										popupsMountPoint:
											document.getElementById('editor-comments-sidebar') || document.body,
									}
								: {})}
							isOffline={isOffline}
							customAriaLabel={
								fg('confluence_frontend_comments_a11y_kb')
									? formatMessage(i18n.commentReplyAriaLabel, {
											name: topCommentDisplayName,
										})
									: undefined
							}
						/>
					</NewReplyContainer>
				)}
			</Fragment>
			{isCommentAuthor && isFirstComment && (
				<ThirdPartyNudge
					trigger="inline-comments"
					showImage={false}
					width={280}
					reference={nudgeRef}
				/>
			)}
		</CommentSidebar>
	);
};

export const InlineComment = withFlags(InlineCommentComponent);
